import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import * as youtubeApi from '../../services/youtube';

function YoutubeTrends() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // 상태
  const [activeTab, setActiveTab] = useState('hot');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 날짜 및 시간대 관련
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [currentSlot, setCurrentSlot] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');

  // 데이터
  const [hotVideos, setHotVideos] = useState([]);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [slotKeywords, setSlotKeywords] = useState({ keywords: [], autoKeywords: [] });
  const [slotTrend, setSlotTrend] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [videos, setVideos] = useState([]);
  const [keywords, setKeywords] = useState({ stats: [], activeKeywords: [] });
  const [recommendations, setRecommendations] = useState(null);
  const [status, setStatus] = useState(null);

  // 필터
  const [period, setPeriod] = useState('daily');
  const [videoFilter, setVideoFilter] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');

  // 시간대 목록 로드
  const loadTimeSlots = useCallback(async () => {
    try {
      const response = await youtubeApi.getTimeSlots();
      if (response.data) {
        setTimeSlots(response.data.slots || []);
        setCurrentSlot(response.data.currentSlot || '');
        setSelectedSlot(response.data.currentSlot || '');
      }
    } catch (err) {
      console.error('시간대 로드 오류:', err);
    }
  }, []);

  // HOT 영상 로드
  const loadHotVideos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await youtubeApi.getHotVideos(1, 20);
      setHotVideos(response.data?.videos || []);
    } catch (err) {
      console.error('HOT 영상 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 시간대별 트렌딩 영상 로드
  const loadTrending = useCallback(async () => {
    if (!selectedSlot) return;
    try {
      setLoading(true);
      const response = await youtubeApi.getTrending({ timeSlot: selectedSlot, date: selectedDate, limit: 30 });
      setTrendingVideos(response.data?.videos || []);
    } catch (err) {
      console.error('트렌딩 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSlot, selectedDate]);

  // 시간대별 키워드 로드
  const loadSlotKeywords = useCallback(async () => {
    if (!selectedSlot) return;
    try {
      setLoading(true);
      const response = await youtubeApi.getKeywordsBySlot({ timeSlot: selectedSlot, date: selectedDate });
      setSlotKeywords(response.data || { keywords: [], autoKeywords: [] });
    } catch (err) {
      console.error('키워드 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSlot, selectedDate]);

  // 시간대별 트렌드 요약 로드
  const loadSlotTrend = useCallback(async () => {
    if (!selectedSlot) return;
    try {
      setLoading(true);
      const response = await youtubeApi.getSlotTrend({ timeSlot: selectedSlot, date: selectedDate });
      setSlotTrend(response.data);
    } catch (err) {
      console.error('트렌드 요약 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSlot]);

  // 트렌드 데이터 로드
  const loadTrends = useCallback(async () => {
    try {
      setLoading(true);
      const response = await youtubeApi.getTrends(period);
      console.log('[YoutubeTrends] loadTrends response:', response);
      setTrendData(response.data);
    } catch (err) {
      console.error('트렌드 로드 오류:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  // 영상 목록 로드
  const loadVideos = useCallback(async () => {
    try {
      setLoading(true);
      const options = {
        limit: 30,
        sort: 'view_count',
        order: 'desc'
      };
      if (videoFilter !== 'all') {
        options.source_type = videoFilter;
      }
      if (searchKeyword) {
        options.keyword = searchKeyword;
      }
      const response = await youtubeApi.getVideos(options);
      setVideos(response.data || []);
    } catch (err) {
      console.error('영상 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  }, [videoFilter, searchKeyword]);

  // 키워드 통계 로드
  const loadKeywords = useCallback(async () => {
    try {
      setLoading(true);
      const response = await youtubeApi.getKeywords(7);
      setKeywords(response.data || { stats: [], activeKeywords: [] });
    } catch (err) {
      console.error('키워드 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 추천 로드
  const loadRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await youtubeApi.getRecommendations();
      console.log('[YoutubeTrends] loadRecommendations response:', response);
      setRecommendations(response.data);
    } catch (err) {
      console.error('추천 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 상태 로드
  const loadStatus = useCallback(async () => {
    try {
      const response = await youtubeApi.getStatus();
      setStatus(response.data);
    } catch (err) {
      console.error('상태 로드 오류:', err);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    loadTimeSlots();
    loadStatus();
  }, [loadTimeSlots, loadStatus]);

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    setError(null);
    switch (activeTab) {
      case 'hot':
        loadHotVideos();
        break;
      case 'trending':
        loadTrending();
        loadSlotTrend();
        break;
      case 'keywords':
        loadSlotKeywords();
        break;
      case 'daily':
        loadTrends();
        break;
      case 'videos':
        loadVideos();
        break;
      case 'all-keywords':
        loadKeywords();
        break;
      case 'recommendations':
        loadRecommendations();
        break;
      default:
        break;
    }
  }, [activeTab, selectedSlot, loadHotVideos, loadTrending, loadSlotTrend, loadSlotKeywords, loadTrends, loadVideos, loadKeywords, loadRecommendations]);

  // 수동 수집 트리거
  const handleCollect = async () => {
    if (!user || user.tier !== 'admin') {
      alert('관리자 권한이 필요합니다.');
      return;
    }
    try {
      await youtubeApi.triggerCollection('both');
      alert('수집이 시작되었습니다. 잠시 후 새로고침해주세요.');
    } catch (err) {
      alert('수집 시작 실패: ' + err.message);
    }
  };

  // 수동 분석 트리거
  const handleAnalyze = async () => {
    if (!user || user.tier !== 'admin') {
      alert('관리자 권한이 필요합니다.');
      return;
    }
    try {
      await youtubeApi.triggerAnalysis('slot', null, selectedSlot);
      await youtubeApi.triggerAnalysis('hot_scores', null, selectedSlot);
      alert('분석이 완료되었습니다.');
      loadHotVideos();
      loadTrending();
    } catch (err) {
      alert('분석 실패: ' + err.message);
    }
  };

  // 숫자 포맷
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  // 날짜 포맷
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  // HOT 레벨 표시
  const getHotLevel = (score) => {
    if (score >= 90) return { level: 'ultra', label: 'ULTRA HOT' };
    if (score >= 70) return { level: 'hot', label: 'HOT' };
    if (score >= 50) return { level: 'rising', label: 'RISING' };
    return { level: '', label: '' };
  };

  return (
    <div className="max-w-[1200px] mx-auto p-6 min-h-screen bg-slate-50">
      {/* 헤더 */}
      <div className="flex justify-between items-start mb-6 p-6 bg-[#1d1d1f] rounded-xl text-white">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold m-0 mb-2 text-white">YouTube 트렌드 분석</h1>
          <p className="text-[0.95rem] m-0 text-white/85">6시간 주기 실시간 트렌드 분석</p>
        </div>

        {/* 상태 표시 */}
        {status && (
          <div className="flex gap-6 bg-white/15 py-3 px-5 rounded-lg">
            <span className="flex flex-col items-center gap-1">
              <span className="text-xs text-white/85 uppercase font-medium">영상</span>
              <span className="text-[15px] font-bold text-white">{formatNumber(status.collection?.totals?.videos || 0)}</span>
            </span>
            <span className="flex flex-col items-center gap-1">
              <span className="text-xs text-white/85 uppercase font-medium">키워드</span>
              <span className="text-[15px] font-bold text-white">{status.collection?.totals?.keywords || 0}</span>
            </span>
            <span className="flex flex-col items-center gap-1">
              <span className="text-xs text-white/85 uppercase font-medium">오늘 수집</span>
              <span className="text-[15px] font-bold text-white">{status.collection?.today?.videosCollected || 0}</span>
            </span>
          </div>
        )}
      </div>

      {/* 날짜 및 시간대 선택 */}
      <div className="flex flex-col gap-3 mb-4 p-4 bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[0.9rem] font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">날짜</span>
          <div className="flex gap-1.5 flex-wrap">
            {[0, 1, 2, 3, 4, 5, 6].map((daysAgo) => {
              const date = new Date();
              date.setDate(date.getDate() - daysAgo);
              const dateStr = date.toISOString().split('T')[0];
              const label = daysAgo === 0 ? '오늘' : daysAgo === 1 ? '어제' : `${daysAgo}일전`;
              return (
                <button
                  key={daysAgo}
                  className={`py-2 px-3 min-h-[36px] bg-transparent border border-gray-200 rounded-lg text-[0.85rem] font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 ${selectedDate === dateStr ? 'bg-[#1d1d1f] border-[#1d1d1f] text-white hover:bg-[#1d1d1f] hover:text-white' : ''}`}
                  onClick={() => setSelectedDate(dateStr)}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="py-2 px-3 min-h-[36px] border border-slate-200 rounded-md text-[13px] text-gray-900 bg-slate-50 cursor-pointer transition-all duration-200 hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:bg-white"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[0.9rem] font-semibold text-gray-500 uppercase tracking-wide">시간대</span>
          <div className="flex gap-2 flex-wrap">
            {timeSlots.map((slot) => (
              <button
                key={slot.slot}
                className={`relative py-3 px-4 min-h-[44px] bg-transparent border border-gray-200 rounded-lg text-[0.95rem] font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 ${selectedSlot === slot.slot ? 'bg-[#1d1d1f] border-[#1d1d1f] text-white hover:bg-[#1d1d1f] hover:text-white' : ''} ${slot.isCurrent ? 'border-emerald-500' : ''}`}
                onClick={() => setSelectedSlot(slot.slot)}
              >
                {slot.slot}
                {slot.isCurrent && selectedDate === new Date().toISOString().split('T')[0] && <span className="absolute -top-2 -right-2 py-0.5 px-1.5 bg-emerald-500 text-white text-xs font-bold rounded">NOW</span>}
                {slot.hasData && <span className="absolute bottom-1 right-1 w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 pb-2">
        <button
          className={`py-3 px-6 min-h-[44px] bg-none border-none rounded-t-lg text-base font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:text-gray-700 hover:bg-gray-100 focus:outline focus:outline-2 focus:outline-[#1d1d1f] focus:outline-offset-2 ${activeTab === 'hot' ? 'text-[#1d1d1f] bg-gray-100' : ''}`}
          onClick={() => setActiveTab('hot')}
        >
          HOT 영상
        </button>
        <button
          className={`py-3 px-6 min-h-[44px] bg-none border-none rounded-t-lg text-base font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:text-gray-700 hover:bg-gray-100 focus:outline focus:outline-2 focus:outline-[#1d1d1f] focus:outline-offset-2 ${activeTab === 'trending' ? 'text-[#1d1d1f] bg-gray-100' : ''}`}
          onClick={() => setActiveTab('trending')}
        >
          트렌딩
        </button>
        <button
          className={`py-3 px-6 min-h-[44px] bg-none border-none rounded-t-lg text-base font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:text-gray-700 hover:bg-gray-100 focus:outline focus:outline-2 focus:outline-[#1d1d1f] focus:outline-offset-2 ${activeTab === 'keywords' ? 'text-[#1d1d1f] bg-gray-100' : ''}`}
          onClick={() => setActiveTab('keywords')}
        >
          키워드
        </button>
        <button
          className={`py-3 px-6 min-h-[44px] bg-none border-none rounded-t-lg text-base font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:text-gray-700 hover:bg-gray-100 focus:outline focus:outline-2 focus:outline-[#1d1d1f] focus:outline-offset-2 ${activeTab === 'daily' ? 'text-[#1d1d1f] bg-gray-100' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          리포트
        </button>
        <button
          className={`py-3 px-6 min-h-[44px] bg-none border-none rounded-t-lg text-base font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:text-gray-700 hover:bg-gray-100 focus:outline focus:outline-2 focus:outline-[#1d1d1f] focus:outline-offset-2 ${activeTab === 'recommendations' ? 'text-[#1d1d1f] bg-gray-100' : ''}`}
          onClick={() => setActiveTab('recommendations')}
        >
          추천
        </button>
      </div>

      {/* 관리자 컨트롤 */}
      {user?.tier === 'admin' && (
        <div className="flex gap-3 mb-4 p-4 bg-gray-100 rounded-lg border border-gray-200">
          <button className="py-3 px-6 min-h-[44px] bg-[#1d1d1f] border-none rounded-lg text-[0.95rem] font-medium text-white cursor-pointer transition-all duration-200 hover:bg-gray-700 active:bg-gray-600 disabled:bg-gray-400 disabled:text-gray-300 disabled:cursor-not-allowed" onClick={handleCollect}>
            데이터 수집
          </button>
          <button className="py-3 px-6 min-h-[44px] bg-[#1d1d1f] border-none rounded-lg text-[0.95rem] font-medium text-white cursor-pointer transition-all duration-200 hover:bg-gray-700 active:bg-gray-600 disabled:bg-gray-400 disabled:text-gray-300 disabled:cursor-not-allowed" onClick={handleAnalyze}>
            AI 분석 ({selectedSlot})
          </button>
        </div>
      )}

      {/* 콘텐츠 */}
      <div className="bg-white rounded-lg p-6 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
        {loading && <div className="text-center py-12 text-gray-500 text-[15px]">로딩 중...</div>}
        {error && <div className="text-center py-6 text-red-600 bg-red-100 rounded-lg text-[15px]">{error}</div>}

        {/* 디버그 정보 (임시) */}
        <div style={{ background: '#fff3cd', padding: '8px', fontSize: '11px', marginBottom: '16px', borderRadius: '4px', color: '#856404' }}>
          [Debug] tab={activeTab}, loading={String(loading)},
          trendData={trendData?.summary ? '✓' : '✗'},
          recommendations={recommendations?.recommendations?.length > 0 ? '✓' : '✗'}
        </div>

        {/* HOT 영상 탭 */}
        {activeTab === 'hot' && !loading && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 m-0 mb-2">오늘의 HOT 영상</h2>
              <p className="text-[15px] text-gray-500 m-0">조회수 급상승 + AI 분석 + 댓글/좋아요 변화 종합</p>
            </div>

            <div className="flex flex-col gap-3">
              {hotVideos.length > 0 ? (
                hotVideos.map((video, index) => {
                  const hotLevel = getHotLevel(video.hot_score);
                  return (
                    <div key={video.video_id} className={`flex items-center gap-4 p-4 bg-slate-50 rounded-lg transition-all duration-200 border-2 border-transparent hover:bg-blue-50 hover:border-blue-500 ${video.isHot ? 'bg-red-100 border-red-500' : ''}`}>
                      <div className={`w-8 h-8 flex items-center justify-center bg-blue-500 text-white text-[15px] font-bold rounded-full flex-shrink-0 ${video.isHot ? 'bg-red-500' : ''}`}>{index + 1}</div>
                      <div className="flex-1 min-w-0">
                        <a
                          href={`https://youtube.com/watch?v=${video.video_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[15px] font-semibold text-gray-900 no-underline block mb-2 leading-normal overflow-hidden text-ellipsis whitespace-nowrap hover:text-blue-500"
                        >
                          {video.title}
                        </a>
                        <div className="flex items-center gap-4 text-[15px] text-gray-500">
                          <span className="text-gray-500">{video.channel_name}</span>
                          <span className="flex items-center gap-2">
                            {formatNumber(video.view_count)}회
                            {video.view_change_rate > 0 && (
                              <span className="text-xs font-semibold py-0.5 px-1.5 rounded bg-green-100 text-green-600">
                                +{video.view_change_rate.toFixed(1)}%
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className={`flex flex-col items-center gap-1 py-2 px-3 bg-gray-100 rounded-lg border border-gray-200 min-w-[60px] ${hotLevel.level === 'ultra' ? 'bg-[#1d1d1f] border-[#1d1d1f]' : ''} ${hotLevel.level === 'hot' ? 'bg-red-100 border-red-500' : ''} ${hotLevel.level === 'rising' ? 'bg-green-100 border-emerald-500' : ''}`}>
                        {hotLevel.label && <span className={`text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 ${hotLevel.level === 'ultra' ? 'text-white' : ''} ${hotLevel.level === 'hot' ? 'text-red-500' : ''} ${hotLevel.level === 'rising' ? 'text-emerald-500' : ''}`}>{hotLevel.label}</span>}
                        <span className={`text-base font-bold text-[#1d1d1f] ${hotLevel.level === 'ultra' ? 'text-white' : ''} ${hotLevel.level === 'hot' ? 'text-red-500' : ''} ${hotLevel.level === 'rising' ? 'text-emerald-500' : ''}`}>{video.hot_score}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p className="m-0 mb-2 text-[15px]">HOT 영상이 없습니다.</p>
                  <p className="text-xs text-gray-400">데이터 수집 후 확인해주세요.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 트렌딩 영상 탭 */}
        {activeTab === 'trending' && !loading && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 m-0 mb-2">{selectedSlot} 트렌딩</h2>
              {slotTrend?.summary && (
                <div className="bg-blue-50 p-4 rounded-lg mt-3 border-l-4 border-blue-500">
                  {slotTrend.summary.split('\n').slice(0, 3).map((line, i) => (
                    <p key={i} className="m-0 mb-2 text-[15px] text-gray-900 leading-relaxed">{line}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
              {trendingVideos.length > 0 ? (
                trendingVideos.map((video) => (
                  <div key={video.video_id} className={`bg-white rounded-lg p-4 border border-slate-200 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] ${video.isHot ? 'border-red-500 bg-red-100' : ''} ${video.isRising ? 'border-emerald-500 bg-emerald-100' : ''}`}>
                    <a
                      href={`https://youtube.com/watch?v=${video.video_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="no-underline text-inherit"
                    >
                      <div className="flex gap-2 mb-2">
                        {video.isHot && <span className="inline-block py-1 px-2 bg-[#1d1d1f] text-white text-[0.7rem] font-semibold rounded uppercase tracking-wide">HOT</span>}
                        {video.isRising && <span className="inline-block py-1 px-2 bg-emerald-500 text-white text-[0.7rem] font-semibold rounded uppercase tracking-wide">급상승</span>}
                      </div>
                      <h3 className="text-[15px] font-semibold text-gray-900 m-0 mb-2 leading-normal line-clamp-2">{video.title}</h3>
                      <p className="text-[15px] text-gray-500 m-0 mb-3">{video.channel_name}</p>
                      <div className="flex gap-3 text-[0.8rem] text-gray-500 mb-2">
                        <span className="flex items-center gap-1"><span className="font-medium text-gray-400">조회</span> {formatNumber(video.view_count)}</span>
                        <span className="flex items-center gap-1"><span className="font-medium text-gray-400">좋아요</span> {formatNumber(video.like_count)}</span>
                        <span className="flex items-center gap-1"><span className="font-medium text-gray-400">댓글</span> {formatNumber(video.comment_count)}</span>
                      </div>
                      {video.view_change_rate > 0 && (
                        <div className="flex items-center gap-2 p-2 bg-emerald-100 rounded-lg mb-2">
                          <span className="text-xs font-semibold py-0.5 px-1.5 rounded bg-green-100 text-green-600">+{video.view_change_rate.toFixed(1)}%</span>
                          <span className="text-xs text-gray-500">조회수 변화</span>
                        </div>
                      )}
                      {video.extracted_keywords?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {video.extracted_keywords.slice(0, 3).map((kw, i) => (
                            <span key={i} className="inline-flex items-center gap-1 py-1.5 px-3 bg-blue-50 text-blue-500 rounded-full text-[15px]">{kw}</span>
                          ))}
                        </div>
                      )}
                    </a>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>{selectedSlot} 시간대의 트렌딩 영상이 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 키워드 분석 탭 */}
        {activeTab === 'keywords' && !loading && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 m-0 mb-2">실시간 키워드 TOP 30</h2>
              <p className="text-[15px] text-gray-500 m-0">{selectedSlot} 시간대 HOT 점수 기준</p>
            </div>

            {/* 통합 키워드 순위 (30위까지) */}
            <div className="mb-8">
              {slotKeywords.keywords?.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {slotKeywords.keywords.slice(0, 30).map((stat) => (
                    <div key={`${stat.keyword}-${stat.source}`} className={`flex items-center gap-4 py-3 px-4 bg-slate-50 rounded-lg transition-all duration-200 hover:bg-blue-50 ${stat.isHot ? 'bg-red-100 border border-red-500' : ''} ${stat.source === 'auto' ? 'bg-green-50 border-l-[3px] border-l-green-500 hover:bg-green-100' : ''}`}>
                      <span className={`w-7 h-7 flex items-center justify-center bg-blue-500 text-white text-xs font-bold rounded-full ${stat.rank <= 3 ? 'bg-gradient-to-br from-amber-500 to-red-500 text-sm w-8 h-8' : ''}`}>
                        {stat.rank}
                      </span>
                      <span className="flex-1 font-medium text-[15px] text-gray-900 flex items-center gap-1.5">
                        {stat.keyword}
                        {stat.isHot && <span className="text-[0.65rem] font-semibold py-0.5 px-1 bg-[#1d1d1f] text-white rounded uppercase">HOT</span>}
                      </span>
                      <span className={`text-[0.65rem] font-semibold py-0.5 px-1.5 rounded uppercase tracking-wide min-w-[32px] text-center ${stat.source === 'auto' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                        {stat.source === 'auto' ? 'AI' : '등록'}
                      </span>
                      <span className="text-[15px] text-blue-500 font-semibold min-w-[80px] text-right">{formatNumber(stat.total_views)}회</span>
                      <span className={`text-xs font-semibold min-w-[70px] text-right ${stat.trend === 'up' ? 'text-green-600' : ''} ${stat.trend === 'down' ? 'text-red-600' : ''} ${stat.trend === 'stable' || !stat.trend ? 'text-gray-500' : ''}`}>
                        {stat.trend === 'up' ? '↑' : stat.trend === 'down' ? '↓' : '−'}
                        {stat.view_change ? formatNumber(Math.abs(stat.view_change)) : '0'}
                      </span>
                      <div className="w-[100px] flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gradient-to-r from-blue-500 to-red-500 rounded" style={{ width: `${stat.hot_score}%` }}></div>
                        <span className="text-xs font-semibold text-gray-500 min-w-[24px]">{stat.hot_score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p className="m-0 mb-2 text-[15px]">키워드 데이터가 없습니다.</p>
                  <p className="text-xs text-gray-400">데이터 수집 후 확인해주세요.</p>
                </div>
              )}
            </div>

            {/* 범례 */}
            <div className="flex justify-center gap-6 mt-6 pt-4 border-t border-gray-200">
              <span className="flex items-center gap-2 text-[0.85rem] text-gray-500"><span className="text-[0.65rem] font-semibold py-0.5 px-1.5 rounded uppercase bg-gray-100 text-gray-500">등록</span> 등록 키워드</span>
              <span className="flex items-center gap-2 text-[0.85rem] text-gray-500"><span className="text-[0.65rem] font-semibold py-0.5 px-1.5 rounded uppercase bg-blue-50 text-blue-600">AI</span> 자동 추출</span>
            </div>
          </div>
        )}

        {/* 일간 리포트 탭 */}
        {activeTab === 'daily' && !loading && (
          <div>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <div className="flex gap-2">
                <button
                  className={`py-3 px-6 min-h-[44px] bg-transparent border border-gray-200 rounded-lg text-[0.95rem] font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 ${period === 'daily' ? 'bg-[#1d1d1f] text-white border-[#1d1d1f] hover:bg-[#1d1d1f] hover:text-white' : ''}`}
                  onClick={() => setPeriod('daily')}
                >
                  일간
                </button>
                <button
                  className={`py-3 px-6 min-h-[44px] bg-transparent border border-gray-200 rounded-lg text-[0.95rem] font-medium text-gray-500 cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 ${period === 'weekly' ? 'bg-[#1d1d1f] text-white border-[#1d1d1f] hover:bg-[#1d1d1f] hover:text-white' : ''}`}
                  onClick={() => setPeriod('weekly')}
                >
                  주간
                </button>
              </div>
              {user?.tier === 'admin' && (
                <button
                  className="py-3 px-6 min-h-[44px] bg-[#1d1d1f] border-none rounded-lg text-[0.95rem] font-medium text-white cursor-pointer transition-all duration-200 hover:bg-gray-700 active:bg-gray-600"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await youtubeApi.triggerAnalysis(period, null, null);
                      alert(`${period === 'daily' ? '일간' : '주간'} 리포트 생성이 완료되었습니다.`);
                      const response = await youtubeApi.getTrends(period);
                      setTrendData(response.data);
                    } catch (err) {
                      alert('리포트 생성 실패: ' + err.message);
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  {period === 'daily' ? '일간' : '주간'} 리포트 생성
                </button>
              )}
            </div>

            {trendData?.summary ? (
              <div className="bg-slate-50 rounded-lg p-6">
                <div className="text-xs text-gray-500 mb-4 pb-3 border-b border-slate-200">
                  {trendData.date} 기준
                </div>
                <div className="leading-relaxed text-gray-900 text-[15px]">
                  {trendData.summary.split('\n').map((line, i) => (
                    <p key={i} className="m-0 mb-3">{line}</p>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="m-0 mb-2 text-[15px]">아직 생성된 트렌드 리포트가 없습니다.</p>
                <p className="text-xs text-gray-400">
                  {user?.tier === 'admin'
                    ? '위의 "리포트 생성" 버튼을 클릭하여 AI 분석 리포트를 생성하세요.'
                    : '관리자가 AI 분석을 실행하면 트렌드 요약이 생성됩니다.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* 콘텐츠 추천 탭 */}
        {activeTab === 'recommendations' && !loading && (
          <div>
            {recommendations?.recommendations?.length > 0 ? (
              <>
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
                  <span className="text-xs text-gray-500">{recommendations.date} 기준</span>
                  {recommendations.cached && <span className="text-[11px] py-0.5 px-2 bg-gray-100 text-gray-400 rounded">캐시됨</span>}
                </div>
                {recommendations.overall_insight && (
                  <div className="bg-[#1d1d1f] text-white p-6 rounded-xl mb-6">
                    <h3 className="text-xl font-semibold m-0 mb-3 text-white">트렌드 인사이트</h3>
                    <p className="m-0 leading-relaxed text-[0.95rem] text-white/90">{recommendations.overall_insight}</p>
                  </div>
                )}

                <div className="flex flex-col gap-4">
                  {recommendations.recommendations.map((rec, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-6 border-l-4 border-blue-500">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-2xl font-semibold text-gray-900 m-0">{rec.topic}</h4>
                        <span className={`text-[0.75rem] py-1.5 px-3 rounded-full font-medium ${rec.estimated_interest === 'high' ? 'bg-[#1d1d1f] text-white' : ''} ${rec.estimated_interest === 'medium' ? 'bg-gray-100 text-gray-500' : ''} ${rec.estimated_interest === 'low' ? 'bg-gray-200 text-gray-400' : ''}`}>
                          {rec.estimated_interest === 'high' ? '관심도 높음' :
                           rec.estimated_interest === 'medium' ? '관심도 보통' : '관심도 낮음'}
                        </span>
                      </div>
                      <p className="text-[15px] text-gray-500 m-0 mb-3 leading-relaxed">{rec.reason}</p>
                      {rec.related_keywords?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {rec.related_keywords.map((kw, j) => (
                            <span key={j} className="py-1.5 px-3 bg-white border border-slate-200 rounded-lg text-xs text-gray-500">{kw}</span>
                          ))}
                        </div>
                      )}
                      {rec.content_tips?.length > 0 && (
                        <ul className="m-0 pl-6 text-gray-900 text-[15px]">
                          {rec.content_tips.map((tip, j) => (
                            <li key={j} className="mb-2 leading-relaxed">{tip}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="m-0 mb-2 text-[15px]">아직 생성된 추천이 없습니다.</p>
                <p className="text-xs text-gray-400">
                  데이터 수집 후 AI 분석을 실행하면 콘텐츠 추천이 생성됩니다.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default YoutubeTrends;
// Deploy trigger 1768133722
